import React, { useEffect } from "react";
import { Box, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";

const PartnersSection = styled(Box)(({ theme }) => ({
  padding: "0 0 48px 0", // Unified section padding
  background: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("md")]: {
    padding: "0 0 32px 0", // Mobile section padding
  },
  [theme.breakpoints.down("sm")]: {
    padding: "0 0 24px 0", // Small mobile section padding
  },
}));

const PartnersHeaderContainer = styled(Box)(({ theme }) => ({
  width: "1109px",
  maxWidth: "100%",
  padding: "0 24px", // Unified container padding
  margin: "0 auto",
  marginBottom: "24px",
  [theme.breakpoints.down("md")]: {
    marginBottom: "20px", // Mobile gap
    padding: "0 20px", // Mobile padding
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: "16px", // Small mobile gap
    padding: "0 16px", // Small mobile padding
  },
}));

const PartnersHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    gap: "8px",
  },
}));

const PartnersScroller = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  position: "relative",
  width: "100vw",
  marginLeft: "calc(-50vw + 50%)",
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    top: 0,
    width: "60px",
    height: "100%",
    zIndex: 2,
    pointerEvents: "none",
    [theme.breakpoints.up("md")]: {
      width: "100px",
    },
  },
  "&::before": {
    left: 0,
    background: "linear-gradient(to right, #ffffff, rgba(255,255,255,0))",
  },
  "&::after": {
    right: 0,
    background: "linear-gradient(to left, #ffffff, rgba(255,255,255,0))",
  },
}));

const PartnersTrack = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRtl",
})<{ isRtl?: boolean }>(({ theme, isRtl }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  width: "fit-content",
  animation: `scroll-partners${isRtl ? "-rtl" : ""} 25s linear infinite`,
  "&:hover": {
    animationPlayState: "paused",
  },
  "@keyframes scroll-partners": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(calc(-120px * 5 - 12px * 5))" }, // Mobile: 5 logos + gaps
    [theme.breakpoints.up("md")]: {
      "100%": { transform: "translateX(calc(-152px * 5 - 16px * 5))" }, // Desktop: 5 logos + gaps
    },
  },
  "@keyframes scroll-partners-rtl": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(calc(120px * 5 + 12px * 5))" }, // Mobile: 5 logos + gaps (reversed)
    [theme.breakpoints.up("md")]: {
      "100%": { transform: "translateX(calc(152px * 5 + 16px * 5))" }, // Desktop: 5 logos + gaps (reversed)
    },
  },
  [theme.breakpoints.up("md")]: {
    gap: "16px",
  },
}));

const PartnerLogo = styled(Box)(({ theme }) => ({
  width: "120px",
  height: "68px",
  borderRadius: "12px",
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
  [theme.breakpoints.up("sm")]: {
    width: "135px",
    height: "76px",
    borderRadius: "14px",
  },
  [theme.breakpoints.up("md")]: {
    width: "152px",
    height: "84px",
    borderRadius: "16px",
  },
}));

const PartnersSectionComponent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const partnerLogos = [
    <PartnerLogo
      key="partner-1"
      sx={{
        backgroundImage: "url(/images/2d16622171f846a94ca91c0c6ab64f413f6e7914.png)",
        backgroundSize: "164.63% 100%",
        backgroundPosition: "top",
      }}
    />,
    <PartnerLogo
      key="partner-2"
      sx={{
        backgroundImage: "url(/images/phifit.png)",
        backgroundSize: "164.63% 100%",
        backgroundPosition: "top",
      }}
    />,
    <PartnerLogo
      key="partner-3"
      sx={{
        backgroundColor: "#f5f5f5",
        backgroundImage: "url(/images/c3672dac677bfb731a564bca8220b5f9f4a4cca8.png)",
        backgroundSize: "145.35% 86.67%",
        backgroundPosition: "47.94% 50%",
      }}
    />,
    <PartnerLogo
      key="partner-4"
      sx={{
        backgroundColor: "#f5f5f5",
        backgroundImage: "url(/images/green-majlis.png)",
        backgroundSize: "145.35% 86.67%",
        backgroundPosition: "47.94% 50%",
      }}
    />,
    <PartnerLogo
      key="partner-5"
      sx={{
        backgroundImage: "url(/images/d2f19d0407bc664b482d3f23c458dc95117700ab.png)",
        backgroundSize: "127.34% 100%",
        backgroundPosition: "46.58% 0%",
      }}
    />,
    <PartnerLogo key="partner-6" sx={{ backgroundColor: "#f5f5f5", position: "relative", overflow: "hidden" }}>
      <Box sx={{ position: "absolute", inset: "24.45% 17.02% 24.44% 16.72%" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            maskImage: "url(/images/9ffb24ff05a58b1afe875831c118bb6a6a807eeb.svg)",
            maskSize: "101.381px 42.934px",
            maskRepeat: "no-repeat",
            maskPosition: "0px",
            backgroundImage: "url(/images/b415c5e5d488134bdffaf68363577671a7a0ec9b.svg)",
          }}
        />
      </Box>
    </PartnerLogo>,
    <PartnerLogo
      key="partner-7"
      sx={{
        backgroundImage: "url(/images/7b0f1d6d1e293936e8ce4cfd678b351eb62a492d.png)",
        backgroundSize: "168.22% 100%",
        backgroundPosition: "48.63% 0%",
      }}
    />,
  ];

  return (
    <PartnersSection>
      <PartnersHeaderContainer>
        <PartnersHeader>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: { xs: "20px", sm: "22px", md: "24px", lg: "28px" },
              lineHeight: "1.7",
              color: "#3E4044",
            }}
          >
            {t("common.partnersSection.title")}
          </Typography>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "16px" },
              lineHeight: "1.5",
              color: "#3E4044",
              padding: { xs: "0 8px", sm: "0", md: "0" },
            }}
          >
            {t("common.partnersSection.description")}
          </Typography>
        </PartnersHeader>
      </PartnersHeaderContainer>
      <PartnersScroller>
        <PartnersTrack isRtl={isRtl}>
          {/* Create multiple sets for smooth infinite scrolling */}
          {Array.from({ length: 4 }, (_, setIndex) =>
            partnerLogos.map((logo, logoIndex) =>
              React.cloneElement(logo, {
                key: `${logo.key}-set-${setIndex}`,
              })
            )
          )}
        </PartnersTrack>
      </PartnersScroller>
    </PartnersSection>
  );
};

export default PartnersSectionComponent;
